import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { PUBLIC_ROUTE_PATHS } from "@/lib/publicRoutes";

/**
 * Canonical URL + hreflang resolution for SEO.
 *
 * Each public page should have ONE canonical URL that search engines
 * attribute it to. We compute that URL based on the environment so
 * staging never claims to be production:
 *
 *   - Production (live Stripe key)  → https://coachaifit.com
 *   - Staging    (test Stripe key)  → https://staging.coachaifit.com
 *   - Local dev / preview            → window.location.origin
 *
 * The origin can also be explicitly overridden with `VITE_SITE_URL`,
 * matching the convention already used by `vite-plugins/seo-files.ts`
 * for robots.txt / sitemap.xml.
 *
 * We also emit `hreflang` alternates (currently `en` + `x-default`,
 * both pointing at the production URL) so that if staging ever leaks
 * into an index, Google still consolidates ranking signals onto the
 * production URL.
 *
 * Routes not in CANONICAL_ROUTES (auth-gated app pages, 404, etc.)
 * deliberately omit canonical/hreflang and emit `noindex`.
 */

const PRODUCTION_ORIGIN = "https://coachaifit.com";
const STAGING_ORIGIN = "https://staging.coachaifit.com";

// Public, indexable routes come from the shared registry in
// src/lib/publicRoutes.ts so adding a new marketing/blog/docs page in
// one place automatically wires it into canonicals AND the sitemap.
const CANONICAL_ROUTES = PUBLIC_ROUTE_PATHS;

type Environment = "production" | "staging" | "preview";

function detectEnvironment(): Environment {
  // Same heuristic as the seo-files plugin: a live Stripe publishable
  // key (`pk_live_…`) means production, anything else is non-production.
  const token = import.meta.env.VITE_PAYMENTS_CLIENT_TOKEN as
    | string
    | undefined;
  if (token && token.startsWith("pk_live_")) return "production";

  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "coachaifit.com" || host === "www.coachaifit.com") {
      return "production";
    }
    if (host === "staging.coachaifit.com") return "staging";
  }
  return "preview";
}

function resolveOrigin(env: Environment): string {
  // Explicit override wins (used by CI / preview deploys).
  const override = (import.meta.env.VITE_SITE_URL as string | undefined)?.trim();
  if (override) return override.replace(/\/$/, "");

  if (env === "production") return PRODUCTION_ORIGIN;
  if (env === "staging") return STAGING_ORIGIN;
  if (typeof window !== "undefined") {
    return window.location.origin.replace(/\/$/, "");
  }
  return PRODUCTION_ORIGIN;
}

function normalizePath(pathname: string): string {
  // Treat `/pricing/` and `/pricing` as the same route. Root keeps `/`.
  return pathname !== "/" && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
}

export function RouteCanonical() {
  const { pathname } = useLocation();
  const normalized = normalizePath(pathname);

  if (!CANONICAL_ROUTES.has(normalized)) {
    // Auth-gated, transient, or unknown route — explicitly tell crawlers
    // not to index, just in case they reach it via a stray inbound link.
    return (
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
    );
  }

  const env = detectEnvironment();
  const origin = resolveOrigin(env);
  const canonical = `${origin}${normalized}`;
  const productionUrl = `${PRODUCTION_ORIGIN}${normalized}`;

  return (
    <Helmet>
      <link rel="canonical" href={canonical} />
      <meta property="og:url" content={canonical} />
      {/* Point hreflang at the production URL so any indexed staging
          variant consolidates onto the live site. */}
      <link rel="alternate" hrefLang="en" href={productionUrl} />
      <link rel="alternate" hrefLang="x-default" href={productionUrl} />
      {env !== "production" && (
        // Belt-and-braces: keep non-production environments out of the
        // index entirely, even if a crawler ignores the canonical hint.
        <meta name="robots" content="noindex, follow" />
      )}
    </Helmet>
  );
}
